<template>
  <div
    ref="anchor"
    class="video-card"
    @mouseenter="scheduleOpen"
    @mouseleave="scheduleClose"
    @focusin="open"
    @focusout="scheduleClose"
  >
    <slot></slot>

    <teleport to="body">
      <div
        v-if="active"
        class="video-card__panel"
        :style="panelStyle"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      >
        <a class="block text-gray-700 no-underline hover:text-teal-600" :href="url">
          <div class="relative bg-gray-900">
            <img v-if="poster" class="block aspect-video w-full object-cover" :src="poster" :alt="title" />

            <div
              v-if="canPreview"
              class="absolute inset-0 opacity-0 transition-opacity duration-300 ease-out"
              :class="{ 'opacity-100': playing }"
            >
              <div ref="preview"></div>
            </div>
          </div>

          <div class="p-4 border-t border-gray-200">
            <div class="flex items-start justify-between gap-x-4">
              <h3 class="mb-0 text-pretty text-lg font-semibold leading-tight antialiased">{{ title }}</h3>
              <p v-if="duration" class="mb-0 whitespace-nowrap text-sm text-gray-700">{{ duration }}</p>
            </div>

            <p v-if="description" class="video-card__description">{{ description }}</p>
          </div>
        </a>
      </div>
    </teleport>
  </div>
</template>

<script>
// How long the pointer must rest on a card before we commit to loading video.
const OPEN_DELAY = 300;
const CLOSE_DELAY = 120;

// Panel grows past the card by this ratio, evenly in every direction.
const GROWTH = 1.14;
const MIN_WIDTH = 320;
const VIEWPORT_MARGIN = 12;

export default {
  props: {
    title: { type: String, required: true },
    url: { type: String, required: true },
    poster: { type: String, default: null },
    duration: { type: String, default: null },
    description: { type: String, default: null },
    // JW media id — the preview streams from JW's HLS manifest.
    jwMediaId: { type: String, default: null },
  },

  data() {
    return {
      active: false,
      panelStyle: {},
      openTimer: null,
      closeTimer: null,
      player: null,
      playing: false,
    };
  },

  computed: {
    canPreview() {
      return Boolean(this.jwMediaId && window.jwplayer);
    },
  },

  mounted() {
    window.addEventListener("keydown", this.onKeydown);
    // Any layout shift under a floating panel makes it point at the wrong card.
    window.addEventListener("scroll", this.close, { passive: true });
    window.addEventListener("resize", this.close);
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("scroll", this.close);
    window.removeEventListener("resize", this.close);
    this.cancelOpen();
    this.cancelClose();
    this.stopPreview();
  },

  methods: {
    scheduleOpen() {
      this.cancelClose();
      if (this.active) return;
      this.openTimer = setTimeout(this.open, OPEN_DELAY);
    },

    async open() {
      this.cancelOpen();
      this.cancelClose();

      // Touch/coarse pointers have no hover state to speak of — leave them the
      // plain card so a tap navigates instead of opening a panel.
      if (window.matchMedia("(hover: none)").matches) return;

      this.position();
      this.active = true;

      this.startPreview();
    },

    // Preview through JW rather than a progressive mp4: the mp4 renditions are
    // the full-length asset, so the browser buffers the whole file before it can
    // show a frame. JW's HLS manifest is segmented and starts near-instantly.
    async startPreview() {
      if (!this.canPreview) return;

      await this.$nextTick();
      if (!this.active || !this.$refs.preview) return;

      window.jwplayer.key = window.gembaConfig?.jwKey;

      this.player = window.jwplayer(this.$refs.preview).setup({
        playlist: `https://content.jwplatform.com/v2/media/${this.jwMediaId}?format=json`,
        width: "100%",
        aspectratio: "16:9",
        mute: true,
        autostart: true,
        repeat: true,
        controls: false,
        displaytitle: false,
        displaydescription: false,
        // A hover preview should never inherit the floating-player behaviour.
        floating: false,
      });

      // Keep the poster on top until frames are actually flowing — a player that
      // stalls should look like a still card, not an empty box.
      this.player.on("play", () => {
        this.playing = true;
      });
    },

    close() {
      this.cancelOpen();
      this.cancelClose();
      this.stopPreview();
      this.active = false;
    },

    stopPreview() {
      if (!this.player) return;

      try {
        this.player.remove();
      } catch (error) {
        // Player was already torn down with its container.
      }
      this.player = null;
      this.playing = false;
    },

    position() {
      const rect = this.$refs.anchor.getBoundingClientRect();
      const width = Math.max(rect.width * GROWTH, MIN_WIDTH);
      const growX = (width - rect.width) / 2;
      // Same growth ratio vertically, so the card expands evenly about its centre.
      const growY = (rect.height * GROWTH - rect.height) / 2;

      const left = Math.min(
        Math.max(rect.left - growX, VIEWPORT_MARGIN),
        window.innerWidth - width - VIEWPORT_MARGIN
      );

      // The panel is taller than the card it replaces (description + larger
      // media), so estimate its height and keep the whole thing on screen.
      const height = rect.height * GROWTH + (this.description ? 90 : 0);
      const top = Math.min(
        Math.max(rect.top - growY, VIEWPORT_MARGIN),
        window.innerHeight - height - VIEWPORT_MARGIN
      );

      this.panelStyle = {
        width: `${width}px`,
        left: `${left}px`,
        top: `${Math.max(top, VIEWPORT_MARGIN)}px`,
        minHeight: `${rect.height * GROWTH}px`,
      };
    },

    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },

    scheduleClose() {
      this.cancelOpen();
      this.closeTimer = setTimeout(this.close, CLOSE_DELAY);
    },

    cancelOpen() {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    },

    cancelClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    },
  },
};
</script>

<style lang="postcss" scoped>
@keyframes video-card-in {
  from {
    opacity: 0;
    transform: scale(0.94);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.video-card__panel {
  position: fixed;
  z-index: 50;
  animation: video-card-in 150ms ease-out;
  @apply overflow-clip rounded-md bg-white shadow-xl ring-1 ring-gray-800/10;
}

.video-card__panel :deep(.jwplayer) {
  border-radius: 0;
  box-shadow: none;
  background: transparent;
}

.video-card__panel :deep(.jw-media){
  cursor: pointer !important;
}

.video-card__description {
  @apply mb-0 mt-2 text-sm leading-snug text-gray-700;
  display: -webkit-box;
  box-orient: vertical;
  line-clamp: 4;
  overflow: hidden;
}
</style>
